import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowUpCircle, ArrowDownCircle, Target, TrendingUp, DollarSign, Calendar, Bell, Wallet, AlertCircle, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { formatCurrency } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);
  const [allTransactions, setAllTransactions] = useState([]);
  const [upcomingBills, setUpcomingBills] = useState([]);
  const [goals, setGoals] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [netWorth, setNetWorth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('current-month');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [reportsRes, transactionsRes, billsRes, goalsRes, budgetsRes, alertsRes, netWorthRes] = await Promise.all([
        axios.get(`${API}/reports`, { headers }),
        axios.get(`${API}/transactions`, { headers }),
        axios.get(`${API}/bills`, { headers }),
        axios.get(`${API}/goals`, { headers }),
        axios.get(`${API}/budgets`, { headers }),
        axios.get(`${API}/alerts`, { headers }),
        axios.get(`${API}/analytics/net-worth`, { headers })
      ]);

      setReports(reportsRes.data);
      setAllTransactions(transactionsRes.data);
      setUpcomingBills(billsRes.data.slice(0, 3));
      setGoals(goalsRes.data.slice(0, 3));
      setBudgets(budgetsRes.data);
      setAlerts(alertsRes.data.alerts?.slice(0, 3) || []);
      setNetWorth(netWorthRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter transactions by selected period
  const filteredData = useMemo(() => {
    if (!allTransactions.length) return { transactions: [], income: 0, expenses: 0, previousIncome: 0, previousExpenses: 0 };

    const now = new Date();
    let startDate, endDate, prevStartDate, prevEndDate;

    switch (selectedPeriod) {
      case 'current-month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        // Previous month for comparison
        prevStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        prevEndDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case 'last-month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        // Month before last for comparison
        prevStartDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        prevEndDate = new Date(now.getFullYear(), now.getMonth() - 1, 0, 23, 59, 59);
        break;
      case 'this-quarter':
        const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
        startDate = new Date(now.getFullYear(), quarterStartMonth, 1);
        endDate = new Date(now.getFullYear(), quarterStartMonth + 3, 0, 23, 59, 59);
        // Previous quarter
        prevStartDate = new Date(now.getFullYear(), quarterStartMonth - 3, 1);
        prevEndDate = new Date(now.getFullYear(), quarterStartMonth, 0, 23, 59, 59);
        break;
      case 'this-year':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
        // Previous year
        prevStartDate = new Date(now.getFullYear() - 1, 0, 1);
        prevEndDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
        break;
      case 'all-time':
      default:
        startDate = new Date(2000, 0, 1);
        endDate = new Date(2100, 11, 31);
        prevStartDate = null;
        prevEndDate = null;
        break;
    }

    const transactions = allTransactions.filter(t => {
      const tDate = new Date(t.date);
      return tDate >= startDate && tDate <= endDate;
    });

    const prevTransactions = prevStartDate && prevEndDate 
      ? allTransactions.filter(t => {
          const tDate = new Date(t.date);
          return tDate >= prevStartDate && tDate <= prevEndDate;
        })
      : [];

    const income = transactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
    const expenses = transactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
    const previousIncome = prevTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
    const previousExpenses = prevTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);

    // Calculate category breakdown
    const expensesByCategory = {};
    transactions.filter(t => t.type === 'expense').forEach(t => {
      expensesByCategory[t.category] = (expensesByCategory[t.category] || 0) + t.amount;
    });

    return { 
      transactions, 
      income, 
      expenses, 
      previousIncome, 
      previousExpenses,
      expensesByCategory,
      savings: income - expenses
    };
  }, [allTransactions, selectedPeriod]);

  // Calculate comparison percentages
  const incomeChange = filteredData.previousIncome > 0 
    ? ((filteredData.income - filteredData.previousIncome) / filteredData.previousIncome * 100).toFixed(1)
    : 0;
  const expenseChange = filteredData.previousExpenses > 0
    ? ((filteredData.expenses - filteredData.previousExpenses) / filteredData.previousExpenses * 100).toFixed(1)
    : 0;

  const expenseData = Object.entries(filteredData.expensesByCategory || {}).map(([category, amount]) => ({
    name: category,
    value: amount
  }));

  const summaryCards = [
    {
      title: 'Total Income',
      value: formatCurrency(filteredData.income || 0, user?.currency),
      icon: ArrowUpCircle,
      color: 'from-emerald-400 to-teal-500',
      textColor: 'text-emerald-600 dark:text-emerald-400',
      testId: 'total-income',
      change: incomeChange,
      changeDirection: incomeChange > 0 ? 'up' : incomeChange < 0 ? 'down' : 'stable'
    },
    {
      title: 'Total Expenses',
      value: formatCurrency(filteredData.expenses || 0, user?.currency),
      icon: ArrowDownCircle,
      color: 'from-rose-400 to-pink-500',
      textColor: 'text-rose-600 dark:text-rose-400',
      testId: 'total-expenses',
      change: expenseChange,
      changeDirection: expenseChange > 0 ? 'up' : expenseChange < 0 ? 'down' : 'stable'
    },
    {
      title: 'Net Savings',
      value: formatCurrency(filteredData.savings || 0, user?.currency),
      icon: DollarSign,
      color: 'from-blue-400 to-indigo-500',
      textColor: 'text-blue-600 dark:text-blue-400',
      testId: 'net-savings'
    },
    {
      title: 'Net Worth',
      value: formatCurrency(netWorth?.net_worth || 0, user?.currency),
      icon: Wallet,
      color: 'from-purple-400 to-pink-500',
      textColor: 'text-purple-600 dark:text-purple-400',
      testId: 'net-worth'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <Navigation user={user} onLogout={onLogout} currentPage="/dashboard" />
          <div className="text-center py-12" data-testid="loading-spinner">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto">
        <Navigation user={user} onLogout={onLogout} currentPage="/dashboard" />

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2" data-testid="dashboard-title">
              Financial Overview
            </h1>
            <p className="text-gray-600 dark:text-gray-400">Welcome back! Here's your financial summary.</p>
          </div>
          
          {/* Period Selector */}
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-[200px] dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Select Period" />
            </SelectTrigger>
            <SelectContent className="dark:bg-gray-800">
              <SelectItem value="current-month">This Month</SelectItem>
              <SelectItem value="last-month">Last Month</SelectItem>
              <SelectItem value="this-quarter">This Quarter</SelectItem>
              <SelectItem value="this-year">This Year</SelectItem>
              <SelectItem value="all-time">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.title} className="glass p-6 card-hover" data-testid={card.testId}>
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <h3 className="text-sm text-gray-600 dark:text-gray-300 mb-1">{card.title}</h3>
                <p className={`text-2xl font-bold ${card.textColor}`}>{card.value}</p>
                
                {/* Comparison indicator */}
                {card.change !== undefined && selectedPeriod !== 'all-time' && (
                  <div className="mt-2 flex items-center space-x-1">
                    {card.changeDirection === 'up' && <ArrowUp className="w-4 h-4 text-green-600 dark:text-green-400" />}
                    {card.changeDirection === 'down' && <ArrowDown className="w-4 h-4 text-red-600 dark:text-red-400" />}
                    {card.changeDirection === 'stable' && <Minus className="w-4 h-4 text-gray-400" />}
                    <span className={`text-xs font-medium ${
                      card.changeDirection === 'up' ? 'text-green-600 dark:text-green-400' : 
                      card.changeDirection === 'down' ? 'text-red-600 dark:text-red-400' : 
                      'text-gray-400'
                    }`}>
                      {card.change > 0 ? '+' : ''}{card.change}% vs {selectedPeriod === 'current-month' ? 'last month' : 'previous period'}
                    </span>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Expense by Category */}
          <Card className="glass p-6" data-testid="expense-chart">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Expenses by Category</h2>
            {expenseData.length > 0 ? (
              <div className="space-y-3">
                {expenseData.map((item, index) => {
                  const total = expenseData.reduce((sum, d) => sum + d.value, 0);
                  const percentage = (item.value / total * 100).toFixed(1);
                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-gray-800 dark:text-gray-200">{formatCurrency(item.value, user?.currency)}</span>
                          <span className="text-xs text-gray-500 ml-2">({percentage}%)</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className="h-2.5 rounded-full transition-all duration-500"
                          style={{ 
                            width: `${percentage}%`,
                            backgroundColor: COLORS[index % COLORS.length]
                          }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Total Expenses</span>
                    <span className="text-lg font-bold text-rose-600">
                      {formatCurrency(expenseData.reduce((sum, d) => sum + d.value, 0), user?.currency)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-12">No expense data available</p>
            )}
          </Card>

          {/* Recent Transactions */}
          <Card className="glass p-6" data-testid="recent-transactions">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">
              Recent Transactions 
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                ({selectedPeriod === 'current-month' ? 'This Month' : 
                  selectedPeriod === 'last-month' ? 'Last Month' : 
                  selectedPeriod === 'this-quarter' ? 'This Quarter' : 
                  selectedPeriod === 'this-year' ? 'This Year' : 'All Time'})
              </span>
            </h2>
            <div className="space-y-4">
              {filteredData.transactions.slice(0, 5).length > 0 ? (
                filteredData.transactions.slice(0, 5).map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg hover:shadow-md transition-shadow"
                    data-testid={`transaction-${transaction.id}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full ${transaction.type === 'income' ? 'bg-emerald-100 dark:bg-emerald-900' : 'bg-rose-100 dark:bg-rose-900'} flex items-center justify-center`}>
                        {transaction.type === 'income' ? (
                          <ArrowUpCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        ) : (
                          <ArrowDownCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 dark:text-gray-100">{transaction.description}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {transaction.category} • {new Date(transaction.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <p className={`font-semibold ${transaction.type === 'income' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount, user?.currency).replace(/^[₹$€]/, '')}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  No transactions in this period
                </p>
              )}
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="glass p-6 mb-8" data-testid="quick-actions">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/transactions')}
              className="p-4 bg-white dark:bg-gray-800 rounded-xl hover:shadow-lg transition-all text-center"
              data-testid="quick-action-transactions"
            >
              <ArrowUpCircle className="w-8 h-8 text-teal-600 dark:text-teal-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Add Transaction</p>
            </button>
            <button
              onClick={() => navigate('/budgets')}
              className="p-4 bg-white dark:bg-gray-800 rounded-xl hover:shadow-lg transition-all text-center"
              data-testid="quick-action-budgets"
            >
              <Target className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Set Budget</p>
            </button>
            <button
              onClick={() => navigate('/goals')}
              className="p-4 bg-white dark:bg-gray-800 rounded-xl hover:shadow-lg transition-all text-center"
              data-testid="quick-action-goals"
            >
              <TrendingUp className="w-8 h-8 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Track Goals</p>
            </button>
            <button
              onClick={() => navigate('/ai-advisor')}
              className="p-4 bg-white dark:bg-gray-800 rounded-xl hover:shadow-lg transition-all text-center"
              data-testid="quick-action-ai"
            >
              <DollarSign className="w-8 h-8 text-amber-600 dark:text-amber-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">AI Advisor</p>
            </button>
          </div>
        </Card>

        {/* Net Worth & Alerts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Net Worth Widget */}
          <Card className="glass p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Net Worth</h2>
              <button onClick={() => navigate('/net-worth')} className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 text-sm font-medium">
                View Details →
              </button>
            </div>
            <div className="text-center py-6">
              <p className={`text-4xl font-bold ${netWorth?.net_worth >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {formatCurrency(netWorth?.net_worth || 0, user?.currency)}
              </p>
              <p className="text-gray-600 dark:text-gray-400 mt-2">Your Total Net Worth</p>
              <div className="flex justify-center space-x-6 mt-6">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center mx-auto mb-2">
                    <Wallet className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Assets</p>
                  <p className="font-semibold text-emerald-600 dark:text-emerald-400">{formatCurrency(netWorth?.total_assets || 0, user?.currency)}</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-rose-100 dark:bg-rose-900 flex items-center justify-center mx-auto mb-2">
                    <Calendar className="w-6 h-6 text-rose-600 dark:text-rose-400" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Liabilities</p>
                  <p className="font-semibold text-rose-600 dark:text-rose-400">{formatCurrency(netWorth?.total_liabilities || 0, user?.currency)}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Smart Alerts */}
          <Card className="glass p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Bell className="w-5 h-5 text-orange-600" />
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Smart Alerts</h2>
              </div>
              <button onClick={() => navigate('/alerts')} className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 text-sm font-medium">
                View All →
              </button>
            </div>
            <div className="space-y-3">
              {alerts.length > 0 ? (
                alerts.map((alert, index) => (
                  <div key={index} className={`p-3 rounded-lg border-l-4 ${
                    alert.priority === 'high' ? 'bg-red-50 dark:bg-red-900/20 border-red-500' :
                    alert.priority === 'medium' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500' :
                    'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                  }`}>
                    <div className="flex items-start space-x-2">
                      <AlertCircle className={`w-4 h-4 mt-0.5 ${
                        alert.priority === 'high' ? 'text-red-600 dark:text-red-400' :
                        alert.priority === 'medium' ? 'text-amber-600 dark:text-amber-400' :
                        'text-blue-600 dark:text-blue-400'
                      }`} />
                      <div className="flex-1">
                        <p className="font-medium text-gray-800 dark:text-gray-100 text-sm">{alert.title}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{alert.message}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Bell className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No alerts at this time</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Budget Progress & Goals */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Budget Progress */}
          <Card className="glass p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Budget Progress</h2>
              <button onClick={() => navigate('/budgets')} className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 text-sm font-medium">
                Manage →
              </button>
            </div>
            <div className="space-y-4">
              {budgets.length > 0 ? (
                budgets.slice(0, 4).map((budget) => {
                  const spent = budget.spent || 0;
                  const percentage = (spent / budget.amount * 100);
                  return (
                    <div key={budget.id} className="bg-white dark:bg-gray-700 p-3 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-gray-800 dark:text-gray-100">{budget.category}</span>
                        <span className={`text-sm font-semibold ${percentage > 100 ? 'text-red-600 dark:text-red-400' : percentage > 80 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}>
                          {percentage.toFixed(0)}%
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                        <span>{formatCurrency(spent, user?.currency)}</span>
                        <span>{formatCurrency(budget.amount, user?.currency)}</span>
                      </div>
                      <Progress value={Math.min(percentage, 100)} className="h-2" />
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <Target className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No budgets set yet</p>
                </div>
              )}
            </div>
          </Card>

          {/* Goals Progress */}
          <Card className="glass p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Financial Goals</h2>
              <button onClick={() => navigate('/goals')} className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 text-sm font-medium">
                View All →
              </button>
            </div>
            <div className="space-y-4">
              {goals.length > 0 ? (
                goals.map((goal) => {
                  const progress = (goal.current_amount / goal.target_amount * 100);
                  return (
                    <div key={goal.id} className="bg-white dark:bg-gray-700 p-3 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-gray-800 dark:text-gray-100">{goal.title}</span>
                        <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                          {progress.toFixed(0)}%
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                        <span>{formatCurrency(goal.current_amount, user?.currency)}</span>
                        <span>{formatCurrency(goal.target_amount, user?.currency)}</span>
                      </div>
                      <Progress value={Math.min(progress, 100)} className="h-2" />
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <Target className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No goals set yet</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Upcoming Bills */}
        {upcomingBills.length > 0 && (
          <Card className="glass p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Upcoming Bills</h2>
              <button onClick={() => navigate('/bills')} className="text-teal-600 hover:text-teal-700 text-sm font-medium">
                View All →
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {upcomingBills.map((bill) => (
                <div key={bill.id} className="bg-white dark:bg-gray-700 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-900 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 dark:text-gray-100">{bill.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Due: {new Date(bill.due_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <p className="text-xl font-bold text-rose-600 dark:text-rose-400">{formatCurrency(bill.amount, user?.currency)}</p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
      <Footer />
    </div>
  );
}